import { CareTeamSlot as CareTeamFormSlot } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/careTeamSelectionForm/CareTeamSelectionForm'

import { CareTeamSlot, CareTeamSlotAssignment } from '../../../episodes/data/episodesApi'

const NEUROPSYCH_TEAM_SLOT_KEYS = ['NP_INTAKE_COORDINATOR', 'NP_EVALUATION_PSYCHOLOGIST', 'NP_TESTER']
const ALTERNATE_NEUROPSYCH_TESTER_SLOT_KEY = 'NP_ALTERNATE_TESTER'
const CARE_TEAM_SLOT_KEYS = ['NP_CARE_COORDINATOR', 'NP_TESTING_LOGISTICS']
export const ADDITIONAL_SUPPORT_SLOT_KEY = 'NP_ADDITIONAL_SUPPORT'

export type ManageCareTeamFormSlots = {
  neuropsychTeamSlots: CareTeamFormSlot[]
  alternateNeuropsychTesterSlot?: CareTeamFormSlot
  careTeamSlots: CareTeamFormSlot[]
  additionalSupportSlots: CareTeamFormSlot[]
}

export const getAdditionalSupportFormSlotId = (assignment: CareTeamSlotAssignment) =>
  `${ADDITIONAL_SUPPORT_SLOT_KEY}:${assignment.care_team_slot_assignment_id}`

type GetProviderName = (providerLyraId: string) => string
type GetSlotLabel = (slot: CareTeamSlot) => string

const toOption = (assignment: CareTeamSlotAssignment, getProviderName: GetProviderName) => ({
  label: getProviderName(assignment.provider_lyra_id),
  value: assignment.provider_lyra_id,
})

const toFormSlot = (
  slot: CareTeamSlot,
  getProviderName: GetProviderName,
  getSlotLabel: GetSlotLabel,
): CareTeamFormSlot => ({
  id: slot.care_team_slot,
  label: getSlotLabel(slot),
  options: slot.assignments.map((assignment) => toOption(assignment, getProviderName)),
  selectedValue: slot.assignments[0]?.provider_lyra_id,
})

const toFormSlots = (
  slots: CareTeamSlot[],
  keys: string[],
  getProviderName: GetProviderName,
  getSlotLabel: GetSlotLabel,
): CareTeamFormSlot[] =>
  keys.flatMap((key) => {
    const slot = slots.find(({ care_team_slot }) => care_team_slot === key)
    return slot ? [toFormSlot(slot, getProviderName, getSlotLabel)] : []
  })

export const getManageCareTeamFormSlots = (
  slots: CareTeamSlot[],
  getProviderName: GetProviderName,
  getSlotLabel: GetSlotLabel,
): ManageCareTeamFormSlots => {
  const alternateTesterSlot = slots.find(
    ({ care_team_slot, assignments }) =>
      care_team_slot === ALTERNATE_NEUROPSYCH_TESTER_SLOT_KEY && assignments.length > 0,
  )
  const additionalSupportSlot = slots.find(({ care_team_slot }) => care_team_slot === ADDITIONAL_SUPPORT_SLOT_KEY)

  return {
    neuropsychTeamSlots: toFormSlots(slots, NEUROPSYCH_TEAM_SLOT_KEYS, getProviderName, getSlotLabel),
    alternateNeuropsychTesterSlot:
      alternateTesterSlot && toFormSlot(alternateTesterSlot, getProviderName, getSlotLabel),
    careTeamSlots: toFormSlots(slots, CARE_TEAM_SLOT_KEYS, getProviderName, getSlotLabel),
    additionalSupportSlots: (additionalSupportSlot?.assignments ?? []).map((assignment) => ({
      id: getAdditionalSupportFormSlotId(assignment),
      label: additionalSupportSlot ? getSlotLabel(additionalSupportSlot) : '',
      options: [toOption(assignment, getProviderName)],
      selectedValue: assignment.provider_lyra_id,
    })),
  }
}
